import { useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { PhoneCall } from "@phosphor-icons/react";
import { toast } from "sonner";

const nowLocal = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Logs who contacted a website request and moves it out of Submitted Bookings
// into Leads → Website Leads.
export function WebsiteContactDialog({ booking, team, onClose, onDone }) {
  const [salesperson, setSalesperson] = useState("");
  const [contactedAt, setContactedAt] = useState(nowLocal);
  const [status, setStatus] = useState("contacted");
  const [quoted, setQuoted] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!salesperson) { toast.error("Pick who contacted the client"); return; }
    setSaving(true);
    try {
      await api.post(`/bookings/${booking.id}/to-lead`, {
        salesperson,
        contacted_at: contactedAt ? new Date(contactedAt).toISOString() : "",
        status,
        quoted_amount: Number(quoted) || 0,
        notes: notes.trim(),
      });
      toast.success(`Moved to Leads ▸ Website Leads — contacted by ${salesperson}`);
      onDone?.();
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-lg" data-testid="website-contact-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Mark contacted</DialogTitle>
          <DialogDescription>
            {booking.customer_name} · {booking.vehicle_label || "no car set"} — this leaves Submitted Bookings and becomes a Website Lead you can work and convert later.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 mt-2">
          <div className="col-span-2">
            <Label className="label-mono mb-1.5 block">Who contacted them<span className="text-primary"> *</span></Label>
            <Select value={salesperson} onValueChange={setSalesperson}>
              <SelectTrigger className="bg-input border-border" data-testid="website-contact-salesperson"><SelectValue placeholder={team.length ? "Select…" : "No logins have Leads access yet"} /></SelectTrigger>
              <SelectContent>{team.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono mb-1.5 block">Time contacted</Label>
            <Input type="datetime-local" step={300} value={contactedAt} onChange={(e) => setContactedAt(e.target.value)} className="bg-input border-border" data-testid="website-contact-time" />
          </div>
          <div>
            <Label className="label-mono mb-1.5 block">Lead status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="bg-input border-border" data-testid="website-contact-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="contacted">Contacted</SelectItem>
                <SelectItem value="quoted">Quoted</SelectItem>
                <SelectItem value="lost">Lost</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono mb-1.5 block">Quoted amount</Label>
            <Input type="number" value={quoted} onChange={(e) => setQuoted(e.target.value)} placeholder="0" className="bg-input border-border" data-testid="website-contact-quote" />
          </div>
          <div className="col-span-2">
            <Label className="label-mono mb-1.5 block">Notes</Label>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="What was discussed, what's still missing…" className="bg-input border-border" data-testid="website-contact-notes" />
          </div>
        </div>
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="website-contact-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-emerald-600 text-white hover:bg-emerald-600/90" data-testid="website-contact-save">
            <PhoneCall size={14} className="mr-1.5" />{saving ? "Saving…" : "Mark contacted"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
